import * as React from 'react';
import MonacoEditor from 'react-monaco-editor';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

import {rqlBrowserTheme} from './monaco/rql-theme';
import {RQL_MONACO_LANGUAGE} from './monaco/rql-monaco';

export interface MonacoToken {
    type: string | null;
    word: string | null;
}

export const QueryTextInput: React.FC<{
    query: string;
    onQueryChange: (query: string) => void;
}> = ({query, onQueryChange}) => {
    return (
        <textarea
            className="rql-input"
            value={query}
            onChange={(event) => onQueryChange(event.currentTarget.value)}
        />
    );
};

export const QueryMonacoEditorTextInput: React.FC<{
    query: string;
    onQueryChange: (query: string) => void;
    executeQuery: () => void;
    setCursorToken?: (token: MonacoToken) => void;
}> = ({query, onQueryChange, executeQuery, setCursorToken}) => {
    const [editor, setEditor] =
        React.useState<monaco.editor.IStandaloneCodeEditor | null>(null);

    React.useEffect(() => {
        if (editor !== null) {
            editor.addCommand(monaco.KeyCode.Enter, executeQuery);
        }
    }, [editor, executeQuery]);

    return (
        <MonacoEditor
            language="rql"
            theme="rqlBrowserTheme"
            width="99%"
            height="100%"
            value={query}
            options={{
                wordWrap: 'on',
                lineNumbers: 'off',
                quickSuggestions: false,
                fontFamily: 'monospace',
                minimap: {enabled: false},
                lineDecorationsWidth: 0,
                overviewRulerBorder: false,
                fontSize: 16,
                mouseWheelZoom: false,
                occurrencesHighlight: false,
                renderLineHighlight: 'none',
                hideCursorInOverviewRuler: true,
            }}
            onChange={onQueryChange}
            editorDidMount={(editor) => {
                window.addEventListener('resize', () => editor.layout());
                monaco.languages.register({
                    id: 'rql',
                });
                monaco.languages.setMonarchTokensProvider(
                    'rql',
                    RQL_MONACO_LANGUAGE
                );
                monaco.editor.defineTheme('rqlBrowserTheme', rqlBrowserTheme);

                const model = editor.getModel();
                if (model !== null) {
                    monaco.editor.setModelLanguage(model, 'rql');
                    monaco.editor.setTheme('rqlBrowserTheme');
                }

                editor.focus();
                if (setCursorToken) {
                    editor.onDidChangeCursorPosition((e) => {
                        const model = editor.getModel();
                        if (model === null) {
                            return;
                        }

                        // Token list per line
                        const lineTokens = monaco.editor.tokenize(
                            editor.getValue(),
                            'rql'
                        );

                        // Word at current position
                        const wordAtPosition = model.getWordAtPosition(
                            e.position
                        );

                        if (
                            wordAtPosition === null ||
                            lineTokens === undefined
                        ) {
                            setCursorToken({type: null, word: null});
                            return;
                        }

                        const tokenType = lineTokens[
                            e.position.lineNumber - 1 // lineNumber starts at 1
                        ].find(
                            (token) =>
                                token.offset === wordAtPosition.startColumn - 1 // startColumn starts at 1
                        )?.type;

                        if (tokenType !== undefined) {
                            setCursorToken({
                                type: tokenType,
                                word: wordAtPosition.word,
                            });
                        } else {
                            setCursorToken({type: null, word: null});
                        }
                    });
                }
                setEditor(editor);
            }}
        />
    );
};
